import React, {useMemo} from 'react'
import {useSelector} from 'react-redux'
import {getOfferVariant} from 'components/Offer/Shared/utils'
import {useOtaMode} from 'hooks/useOtaMode'
import {
  getAnchorComplete,
  getHotelHasOffers,
  getOfferAccessFlowSettings,
  getOfferEntity,
  getSplitBookingDetails
} from 'modules/sapiSearch/selectors'

import {useIsEmbeddedWithBrand} from '@vio/core/src/_web/brand/hooks/useIsEmbeddedWithBrand'
import {getClosestDataIdToEventTarget} from '@vio/core/src/_web/utils/browser'
import {TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY} from '@vio/core/src/auth/modules/actions'
import {getHasFreeCancellation} from '@vio/core/src/offer/business/cancellationPenalties'
import getIsBofhOffer from '@vio/core/src/offer/business/isBofhOffer'
import {hasAnchorPrice as calculateHasAnchorPrice} from '@vio/core/src/offer/business/price'
import {useIsHydrated} from '@vio/core/src/utils/react/hooks/useIsHydrated'

import {getAnchorPriceTotal} from '../../modules/sapiSearch/utils'
import {SelectedElement} from '../../utils/selectedElement'
import useRedirectLink from '../useRedirectLink'

const MAX_PROVIDER_CODES = 3

interface Props {
  hotelId: string
  isAnchor: boolean
  onClick: (sourceComponent: string) => void
}
export const useBoVioFirstOfferContainer = ({
  hotelId,
  isAnchor,
  onClick
}: Props) => {
  const {isOTAMode} = useOtaMode()
  const hotelHasOffers = useSelector(state => getHotelHasOffers(state, hotelId))
  const anchorComplete = useSelector(getAnchorComplete)
  const offerEntity = useSelector(state => getOfferEntity(state, hotelId))
  const allOffers = useMemo(() => offerEntity?.offers ?? [], [offerEntity])
  const anchorPriceTotal = getAnchorPriceTotal(offerEntity)

  const [topOffer, ...otherOffers] = allOffers

  const isBofhOffer = getIsBofhOffer(topOffer)
  const hasFreeCancellation = getHasFreeCancellation(
    topOffer?.cancellationPenalties
  )
  const {isAppRequired} = useSelector(state =>
    getOfferAccessFlowSettings(state, hotelId, topOffer?.id)
  )
  const offerVariant = useMemo(() => {
    return getOfferVariant({
      isCheapest: true, // we always get the cheapest offer first
      isBofhOffer
    })
  }, [isBofhOffer])

  const otherOffersMetadata = useMemo(() => {
    if (isOTAMode) {
      return []
    }

    return (
      [...otherOffers]?.slice(0, MAX_PROVIDER_CODES)?.map(offer => ({
        providerCode: offer?.providerCode,
        brandInfo: offer?.metadata.brandInfo
      })) ?? []
    )
  }, [isOTAMode, otherOffers])

  const cheapestOfferFromOthers = useMemo(() => {
    if (isOTAMode) {
      return undefined
    }

    const sortedOffers = [...otherOffers].sort(
      (a, b) => a.totalRate - b.totalRate
    )
    return sortedOffers?.[0] ?? undefined
  }, [isOTAMode, otherOffers])

  const {
    hasSplitBookingOffer,
    hasPrimarySplitBookingOffer,
    showSplitBookingLoader,
    splitBookingOffers,
    splitBookingBundle
  } = useSelector(state => getSplitBookingDetails(state, hotelId))

  const isSoldOut = useMemo(
    () =>
      anchorComplete &&
      !hotelHasOffers &&
      isAnchor &&
      !hasSplitBookingOffer &&
      !showSplitBookingLoader,
    [
      anchorComplete,
      hotelHasOffers,
      isAnchor,
      hasSplitBookingOffer,
      showSplitBookingLoader
    ]
  )
  const isLoading = useMemo(() => {
    if (isSoldOut) return false
    if (isAnchor && showSplitBookingLoader) return true
    if (hasPrimarySplitBookingOffer) return false
    return isOTAMode ? !topOffer : !hotelHasOffers
  }, [
    isOTAMode,
    isSoldOut,
    isAnchor,
    hotelHasOffers,
    topOffer,
    showSplitBookingLoader,
    hasPrimarySplitBookingOffer
  ])
  const hydrated = useIsHydrated()
  const offerLink = useRedirectLink(
    {anchorPriceTotal, offer: topOffer, hotelId},
    hydrated
  )
  const isEmbedded = useIsEmbeddedWithBrand()

  const onOfferClick = (event: React.MouseEvent) => {
    if (isBofhOffer) {
      setTimeout(() => {
        window.open(offerLink, isEmbedded ? '_self' : '_blank')
      }, TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY)
    } else {
      const selectedElement =
        getClosestDataIdToEventTarget(event) || SelectedElement.BlankSpace
      onClick(selectedElement)
    }
  }

  // This logic make sure that we only pass the primaryOffer if it exist
  const primaryOffer = topOffer
    ? {
        ...topOffer,
        hasAnchorPrice:
          cheapestOfferFromOthers?.totalRate && topOffer?.totalRate
            ? calculateHasAnchorPrice(
                topOffer.totalRate,
                cheapestOfferFromOthers.totalRate
              )
            : false
      }
    : undefined

  return {
    isLoading,
    isSoldOut,
    primaryOffer,
    cheapestOfferFromOthers,
    offerVariant,
    hasFreeCancellation,
    isAppRequired,
    onOfferClick,
    otherOffersMetadata,
    isOTAMode,
    hasPrimarySplitBookingOffer,
    splitBookingOffers,
    splitBookingBundle
  }
}
